import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "@/config";
import { GET_METAOBJECT_BY_HANDLE } from "@/lib/shopify/queries";
import { DEFAULT_CAPACITY_BANNER, withDefaultCopy } from "@/shared/utils/capacityCopy";
import {
  TMetaObjectBannerSection,
  TMetaObjectMainTrackConfig,
  TMetaObjectMiniTrackConfig,
  TMetaObjectSeatsAndRacesConfig,
  TUpsellModalConfig,
  TBottomSummaryConfig,
  TDateTimeConfig,
} from "@/types";

export interface MetaObjectConfigData {
  bannerSection: TMetaObjectBannerSection;
  mainTrackConfig: TMetaObjectMainTrackConfig;
  miniTrackConfig: TMetaObjectMiniTrackConfig;
  upsellModalConfig: TUpsellModalConfig;
  juniorTrackConfig: TMetaObjectSeatsAndRacesConfig;
  intermediateTrackConfig: TMetaObjectSeatsAndRacesConfig;
  battleKartConfig: TMetaObjectSeatsAndRacesConfig;
  bottomSummary: TBottomSummaryConfig;
  dateTimeConfig?: TDateTimeConfig;
}

const normalizeLabel = (value: unknown, fallback: string): string =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

/**
 * Validates and normalizes a raw config object (a single entry of the booking
 * config array) into a fully-formed MetaObjectConfigData.
 * @param configData - A single parsed config entry
 * @returns MetaObjectConfigData | null - normalized config, or null if invalid
 */
const normalizeConfigData = (configData: Partial<MetaObjectConfigData> | undefined | null): MetaObjectConfigData | null => {
  if (!configData) {
    return null;
  }

  const { bannerSection, mainTrackConfig, miniTrackConfig, upsellModalConfig, juniorTrackConfig, intermediateTrackConfig, battleKartConfig, bottomSummary, dateTimeConfig } = configData;

  // Validate that all required configurations are present
  // Note: intermediateTrackConfig is optional and will use juniorTrackConfig as fallback if not present
  if (!bannerSection || !mainTrackConfig || !miniTrackConfig || !upsellModalConfig || !juniorTrackConfig || !battleKartConfig) {
    return null;
  }

  return {
    bannerSection: bannerSection as TMetaObjectBannerSection,
    mainTrackConfig: mainTrackConfig as TMetaObjectMainTrackConfig,
    miniTrackConfig: miniTrackConfig as TMetaObjectMiniTrackConfig,
    upsellModalConfig: upsellModalConfig as TUpsellModalConfig,
    juniorTrackConfig: juniorTrackConfig as TMetaObjectSeatsAndRacesConfig,
    // Use intermediateTrackConfig if available, otherwise fall back to juniorTrackConfig
    intermediateTrackConfig: (intermediateTrackConfig || juniorTrackConfig) as TMetaObjectSeatsAndRacesConfig,
    battleKartConfig: battleKartConfig as TMetaObjectSeatsAndRacesConfig,
    bottomSummary: {
      viewLabel: normalizeLabel(bottomSummary?.viewLabel, "View Summary"),
      hideLabel: normalizeLabel(bottomSummary?.hideLabel, "Hide Summary"),
    },
    dateTimeConfig: {
      heading: normalizeLabel(dateTimeConfig?.heading, "For {track}, what day and time?"),
      layoutMessage: normalizeLabel(dateTimeConfig?.layoutMessage, "On {date} we are running our {layout}"),
      capacityBanner: withDefaultCopy(dateTimeConfig?.capacityBanner, DEFAULT_CAPACITY_BANNER),
    },
  };
};

// JSON-escape sequences for the common control characters.
const CONTROL_CHAR_ESCAPES: Record<string, string> = {
  "\b": "\\b",
  "\f": "\\f",
  "\n": "\\n",
  "\r": "\\r",
  "\t": "\\t",
};

/**
 * Returns true if the character at `index` is escaped by an odd number of
 * preceding backslashes (i.e. a real JSON escape, not an escaped backslash).
 */
const isEscapedAt = (str: string, index: number): boolean => {
  let backslashes = 0;
  let cursor = index - 1;
  while (cursor >= 0 && str[cursor] === "\\") {
    backslashes++;
    cursor--;
  }
  return backslashes % 2 === 1;
};

/**
 * Best-effort repair of an inline `data-booking-config` value into valid JSON.
 *
 * The attribute is HTML-entity encoded, so once the browser decodes it we can
 * end up with two kinds of invalid JSON inside string values:
 *   1. Unescaped double quotes from double-quoted HTML attributes
 *      (`<p class="...">`), which decode from `&quot;`.
 *   2. Literal control characters (newlines/tabs) from multi-line HTML blocks.
 *
 * This single pass tracks whether we're inside a JSON string (and inside an
 * HTML tag) so it can escape in-string HTML attribute quotes and in-string
 * control characters while leaving structural quotes and structural whitespace
 * untouched. The Shopify metaobject uses single-quoted, single-line HTML and
 * parses directly, so this only matters for inline configs.
 * @param raw - The raw, decoded JSON-ish string
 * @returns string - A repaired string suitable for JSON.parse
 */
export const repairInlineConfigJson = (raw: string): string => {
  let result = "";
  let insideString = false;
  let insideTag = false;

  for (let i = 0; i < raw.length; i++) {
    const char = raw[i];

    if (!insideString) {
      if (char === '"') {
        insideString = true;
        insideTag = false;
      }
      result += char;
      continue;
    }

    // Inside a JSON string literal.
    if (char === "<") {
      insideTag = true;
      result += char;
    } else if (char === ">") {
      insideTag = false;
      result += char;
    } else if (char === '"') {
      if (isEscapedAt(raw, i)) {
        // Already-escaped quote: keep as-is and stay in the string.
        result += char;
      } else if (insideTag) {
        // Unescaped HTML attribute quote: escape it and stay in the string.
        result += '\\"';
      } else {
        // Structural closing quote.
        insideString = false;
        insideTag = false;
        result += char;
      }
    } else if (CONTROL_CHAR_ESCAPES[char]) {
      result += CONTROL_CHAR_ESCAPES[char];
    } else if (char.charCodeAt(0) < 0x20) {
      result += "\\u" + char.charCodeAt(0).toString(16).padStart(4, "0");
    } else {
      result += char;
    }
  }

  return result;
};

/**
 * Parses a raw JSON config string (same shape used by both the inline
 * `data-booking-config` attribute and the Shopify metaobject field value)
 * and returns the first, normalized config entry.
 *
 * Falls back to a repair pass (see `repairInlineConfigJson`) if the raw value
 * fails to parse directly, so inline configs containing double-quoted or
 * multi-line HTML can still be recovered.
 * @param rawValue - The raw JSON string (an array of config entries)
 * @returns MetaObjectConfigData | null
 */
const parseBookingConfigValue = (rawValue: string): MetaObjectConfigData | null => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(rawValue);
  } catch (initialError) {
    // Retry after repairing HTML attribute quotes and control characters.
    parsed = JSON.parse(repairInlineConfigJson(rawValue));
  }

  const parsedArray = parsed as MetaObjectConfigData[];
  const [configData] = Array.isArray(parsedArray) ? parsedArray : [parsed as MetaObjectConfigData];
  return normalizeConfigData(configData);
};

/**
 * Reads the booking configuration passed inline via the
 * `data-booking-config` attribute on the `#react-product` host element.
 * This is the primary configuration source; the Shopify metaobject is only
 * used as a fallback when this attribute is missing or invalid.
 * @returns MetaObjectConfigData | null - the inline config, or null if unavailable/invalid
 */
const getInlineBookingConfig = (): MetaObjectConfigData | null => {
  try {
    if (typeof document === "undefined") {
      return null;
    }

    const hostElement = document.getElementById("react-product") as HTMLElement | null;
    const rawValue = hostElement?.dataset?.bookingConfig;

    if (!rawValue || !rawValue.trim()) {
      console.info("[booking-config] No inline data-booking-config found; falling back to Shopify metaobject.");
      return null;
    }

    const configData = parseBookingConfigValue(rawValue);

    if (!configData) {
      console.warn("[booking-config] Inline data-booking-config is missing required sections; falling back to metaobject.");
      return null;
    }

    console.info("[booking-config] Using inline data-booking-config from #react-product.");
    return configData;
  } catch (error) {
    console.error("[booking-config] Failed to parse inline data-booking-config; falling back to metaobject:", error);
    return null;
  }
};

/**
 * Fetches metaobject configuration data from the Shopify Storefront API.
 * Used as a fallback when no inline `data-booking-config` is provided.
 * @returns Promise<MetaObjectConfigData | null> - Returns the parsed configuration data or null if failed
 */
const fetchMetaObjectConfigFromShopify = async (): Promise<MetaObjectConfigData | null> => {
  try {
    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: GET_METAOBJECT_BY_HANDLE,
        variables: {
          handle: "product-react-component-config",
          type: "product_react_component_config",
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
    }

    const receivedData = await response.json();

    // Check if the response has the expected structure
    if (!receivedData?.data?.metaobject?.fields) {
      throw new Error("Invalid response structure: missing metaobject fields");
    }

    const fields = receivedData.data.metaobject.fields;
    const [value] = fields;

    if (!value?.value) {
      throw new Error("Invalid response structure: missing field value");
    }

    // Parse the JSON configuration
    const configData = parseBookingConfigValue(value.value);

    if (!configData) {
      throw new Error("Invalid configuration data: missing required config sections");
    }

    return configData;
  } catch (error) {
    console.error("Failed to fetch metaobject configuration:", error);
    return null;
  }
};

/**
 * Resolves the booking configuration, preferring the inline
 * `data-booking-config` attribute and falling back to the Shopify metaobject.
 * @returns Promise<MetaObjectConfigData | null>
 */
const fetchMetaObjectConfig = async (): Promise<MetaObjectConfigData | null> => {
  // 1. Prefer inline config passed via the data-booking-config attribute.
  const inlineConfig = getInlineBookingConfig();
  if (inlineConfig) {
    return inlineConfig;
  }

  // 2. Fall back to fetching from the Shopify metaobject.
  return fetchMetaObjectConfigFromShopify();
};

/**
 * Public resolver for the booking configuration. Prefers the inline
 * `data-booking-config` attribute on `#react-product` and falls back to the
 * Shopify metaobject. Use this anywhere config is needed so the inline config
 * is never bypassed by a direct metaobject fetch.
 * @returns Promise<MetaObjectConfigData | null>
 */
export const resolveBookingConfig = (): Promise<MetaObjectConfigData | null> => fetchMetaObjectConfig();

/**
 * Dispatches metaobject configuration data to the reducer
 * @param configData - The configuration data to dispatch
 * @param dispatch - The dispatch function from useReducer
 */
const dispatchMetaObjectConfig = (configData: MetaObjectConfigData, dispatch: React.Dispatch<any>): void => {
  const { bannerSection, mainTrackConfig, miniTrackConfig, upsellModalConfig, juniorTrackConfig, intermediateTrackConfig, battleKartConfig, bottomSummary, dateTimeConfig } = configData;

  dispatch({
    type: "SET_BOTTOM_SUMMARY_CONFIG",
    payload: {
      bottomSummaryConfig: bottomSummary,
    },
  });

  if (dateTimeConfig) {
    dispatch({
      type: "SET_DATE_TIME_CONFIG",
      payload: {
        dateTimeConfig,
      },
    });
  }

  // Dispatch battle kart track configuration
  dispatch({
    type: "SET_METAOJECT_BATTLE_KART_TRACK_CONFIG",
    payload: {
      metaObjectBattleKartTrackSectionConfig: battleKartConfig,
    },
  });

  // Dispatch track selection configuration
  dispatch({
    type: "SET_METAOBJECT_TRACK_SELECTION_CONFIG",
    payload: {
      metaObjectTrackSelectionConfig: bannerSection,
    },
  });

  // Dispatch main track configuration
  dispatch({
    type: "SET_METAOJECT_MAIN_TRACK_CONFIG",
    payload: {
      metaObjectMainTrackSectionConfig: mainTrackConfig,
    },
  });

  // Dispatch mini track configuration
  dispatch({
    type: "SET_METAOJECT_MINI_TRACK_CONFIG",
    payload: {
      metaObjectMiniTrackSectionConfig: miniTrackConfig,
    },
  });

  // Dispatch upsell modal configuration
  dispatch({
    type: "SET_METAOJECT_UPSELL_MODAL_CONFIG",
    payload: {
      upsellModalConfig: upsellModalConfig,
    },
  });

  // Dispatch junior track configuration
  dispatch({
    type: "SET_METAOJECT_JUNIOR_TRACK_CONFIG",
    payload: {
      metaObjectJuniorTrackSectionConfig: juniorTrackConfig,
    },
  });

  // Dispatch intermediate track configuration
  dispatch({
    type: "SET_METAOJECT_INTERMEDIATE_TRACK_CONFIG",
    payload: {
      metaObjectIntermediateTrackSectionConfig: intermediateTrackConfig,
    },
  });
};

/**
 * Fetches and dispatches metaobject configuration data
 * This is a convenience function that combines fetching and dispatching
 * @param dispatch - The dispatch function from useReducer
 * @returns Promise<boolean> - Returns true if successful, false otherwise
 */
export const fetchAndDispatchMetaObjectConfig = async (dispatch: React.Dispatch<any>): Promise<boolean> => {
  try {
    const configData = await fetchMetaObjectConfig();

    if (configData) {
      dispatchMetaObjectConfig(configData, dispatch);
      return true;
    }

    return false;
  } catch (error) {
    console.error("Failed to fetch and dispatch metaobject configuration:", error);
    return false;
  }
};
